// src/pages/SubscriptionSettings.tsx

import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonCardContent,
  IonButton,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonNote,
  IonSpinner,
  IonBadge,
  IonButtons,
  IonBackButton,
  useIonAlert,
  useIonToast,
} from '@ionic/react';
import {
  checkmarkCircle,
  closeCircle,
  sparkles,
  informationCircle,
  card,
  calendar,
  pricetag
} from 'ionicons/icons';
import React, { useState, useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { usePremium } from '../hooks/usePremium';
import { PremiumPaywall } from '../components/PremiumPaywall';

export const SubscriptionSettings: React.FC = () => {
  const { isPremium, offerings, loading, restorePurchases, subscriptionType, premiumExpiresAt } = usePremium();
  const [showPaywall, setShowPaywall] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [presentAlert] = useIonAlert();
  const [presentToast] = useIonToast();

  const getPlanName = () => {
    if (subscriptionType === 'lifetime') return 'Lifetime Premium';
    if (subscriptionType === 'yearly') return 'Dice Cat Plus Yearly';
    if (subscriptionType === 'monthly') return 'Dice Cat Plus Monthly';
    return 'Free Plan';
  };

  const getPlanPrice = () => {
    if (subscriptionType === 'lifetime') return 'Forever';
    if (subscriptionType === 'yearly') return '$59.99/year';
    if (subscriptionType === 'monthly') return '$6.99/month';
    return 'Free';
  };

  const getNextBillingDate = () => {
    if (subscriptionType === 'lifetime') return 'Never expires';
    if (premiumExpiresAt) {
      return new Date(premiumExpiresAt).toLocaleDateString();
    }
    return 'N/A';
  };

  // Mock subscription info - replace with real data from RevenueCat
  const [subscriptionInfo, setSubscriptionInfo] = useState<any>(null);

  useEffect(() => {
    // Remove mock subscription info since we're using real data from usePremium hook
  }, [isPremium]);

  const handleRestorePurchases = async () => {
    setRestoring(true);
    try {
      const restored = await restorePurchases();
      if (restored) {
        await presentToast({
          message: 'Subscription restored successfully! 🎉',
          duration: 3000,
          color: 'success',
        });
      } else {
        await presentToast({
          message: 'No active subscriptions found to restore.',
          duration: 3000,
          color: 'warning',
        });
      }
    } catch (error) {
      await presentToast({
        message: 'Failed to restore purchases. Please try again.',
        duration: 3000,
        color: 'danger',
      });
    } finally {
      setRestoring(false);
    }
  };

  const handleManageSubscription = () => {
    const platform = Capacitor.getPlatform();
    const isIOS = platform === 'ios';
    const isAndroid = platform === 'android';

    const storeName = isIOS ? 'App Store' : 'Google Play Store';
    const buttonText = isIOS ? 'Open App Store' : 'Open Play Store';
    const subscriptionUrl = isIOS
      ? 'https://apps.apple.com/account/subscriptions'
      : 'https://play.google.com/store/account/subscriptions';

    presentAlert({
      header: 'Manage Subscription',
      message: `To manage your subscription, you'll be redirected to the ${storeName}.`,
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
        },
        {
          text: buttonText,
          handler: () => {
            window.open(subscriptionUrl, '_system');
          },
        },
      ],
    });
  };

  const handleCancelInfo = () => {
    presentAlert({
      header: 'Canceling Your Subscription',
      message: 'If you cancel, you\'ll keep your premium features until the end of your current billing period. After that, you\'ll return to the free plan.',
      buttons: ['Got it'],
    });
  };

  if (loading) {
    return (
      <IonPage>
        <IonHeader>
          <IonToolbar>
            <IonButtons slot="start">
              <IonBackButton defaultHref="/settings" />
            </IonButtons>
            <IonTitle>Subscription</IonTitle>
          </IonToolbar>
        </IonHeader>
        <IonContent className="ion-padding">
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <IonSpinner />
          </div>
        </IonContent>
      </IonPage>
    );
  }

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/settings" />
          </IonButtons>
          <IonTitle>Subscription</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent className="ion-padding">
        {/* Current Status */}
        <IonCard>
          <IonCardHeader>
            <IonCardTitle style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {isPremium ? (
                <>
                  <IonIcon icon={sparkles} color="warning" />
                  Premium Status
                </>
              ) : (
                <>
                  <IonIcon icon={informationCircle} color="medium" />
                  Free Plan
                </>
              )}
            </IonCardTitle>
          </IonCardHeader>
          <IonCardContent>
            <IonList lines="none">
              <IonItem>
                <IonIcon icon={checkmarkCircle} slot="start" color={isPremium ? 'success' : 'medium'} />
                <IonLabel>
                  <h3>Status</h3>
                  <p>{isPremium ? 'Premium Active' : 'Free Plan'}</p>
                </IonLabel>
                <IonBadge color={isPremium ? 'success' : 'medium'} slot="end">
                  {isPremium ? 'Active' : 'Free'}
                </IonBadge>
              </IonItem>

              {isPremium && (
                <>
                  <IonItem>
                    <IonIcon icon={pricetag} slot="start" color="primary" />
                    <IonLabel>
                      <h3>Plan</h3>
                      <p>{getPlanName()}</p>
                    </IonLabel>
                    <IonNote slot="end">{getPlanPrice()}</IonNote>
                  </IonItem>

                  <IonItem>
                    <IonIcon icon={calendar} slot="start" color="primary" />
                    <IonLabel>
                      <h3>{subscriptionType === 'lifetime' ? 'Status' : 'Next Billing Date'}</h3>
                      <p>{getNextBillingDate()}</p>
                    </IonLabel>
                  </IonItem>

                  {subscriptionType === 'lifetime' && (
                    <IonItem lines="none">
                      <IonIcon icon={sparkles} slot="start" color="warning" />
                      <IonLabel>
                        <h3>Alpha Tester</h3>
                        <p>Thank you for being an early supporter! 💙</p>
                      </IonLabel>
                    </IonItem>
                  )}
                </>
              )}
            </IonList>

            {isPremium ? (
              <div style={{ marginTop: '16px' }}>
                {subscriptionType !== 'lifetime' && (
                  <>
                    <IonButton expand="block" fill="outline" onClick={handleManageSubscription}>
                      <IonIcon icon={card} slot="start" />
                      Manage Subscription
                    </IonButton>
                    <IonButton
                      expand="block"
                      fill="clear"
                      size="small"
                      onClick={handleCancelInfo}
                      style={{ marginTop: '8px' }}
                    >
                      How to cancel
                    </IonButton>
                  </>
                )}
                {subscriptionType === 'lifetime' && (
                  <IonNote style={{ display: 'block', textAlign: 'center', marginTop: '16px' }}>
                    Your lifetime premium never expires. Thank you for your support!
                  </IonNote>
                )}
              </div>
            ) : (
              <IonButton
                expand="block"
                color="primary"
                onClick={() => setShowPaywall(true)}
                style={{ marginTop: '16px' }}
              >
                <IonIcon icon={sparkles} slot="start" />
                Upgrade to Premium
              </IonButton>
            )}
          </IonCardContent>
        </IonCard>

        {/* Premium Features Overview */}
        {!isPremium && (
          <IonCard>
            <IonCardHeader>
              <IonCardTitle>Premium Features</IonCardTitle>
            </IonCardHeader>
            <IonCardContent>
              <IonList lines="none" style={{ padding: 0 }}>
                <IonItem>
                  <IonIcon icon={checkmarkCircle} slot="start" color="success" size="small" />
                  <IonLabel>
                    <p style={{ fontSize: '14px' }}>Unlimited Swipes</p>
                  </IonLabel>
                </IonItem>
                <IonItem>
                  <IonIcon icon={checkmarkCircle} slot="start" color="success" size="small" />
                  <IonLabel>
                    <p style={{ fontSize: '14px' }}>Unlimited Groups</p>
                  </IonLabel>
                </IonItem>
                <IonItem>
                  <IonIcon icon={checkmarkCircle} slot="start" color="success" size="small" />
                  <IonLabel>
                    <p style={{ fontSize: '14px' }}>Unlimited Sessions</p>
                  </IonLabel>
                </IonItem>
                <IonItem>
                  <IonIcon icon={checkmarkCircle} slot="start" color="success" size="small" />
                  <IonLabel>
                    <p style={{ fontSize: '14px' }}>Advanced Filters</p>
                  </IonLabel>
                </IonItem>
                <IonItem>
                  <IonIcon icon={checkmarkCircle} slot="start" color="success" size="small" />
                  <IonLabel>
                    <p style={{ fontSize: '14px' }}>Rewind Swipes</p>
                  </IonLabel>
                </IonItem>
                <IonItem>
                  <IonIcon icon={checkmarkCircle} slot="start" color="success" size="small" />
                  <IonLabel>
                    <p style={{ fontSize: '14px' }}>Premium Avatar Items</p>
                  </IonLabel>
                </IonItem>
              </IonList>
            </IonCardContent>
          </IonCard>
        )}

        {/* Restore Purchases */}
        <IonCard>
          <IonCardHeader>
            <IonCardTitle style={{ fontSize: '18px' }}>Restore Purchases</IonCardTitle>
          </IonCardHeader>
          <IonCardContent>
            <p style={{ fontSize: '14px', color: 'var(--ion-color-medium)', marginBottom: '16px' }}>
              If you previously purchased a subscription on another device or after reinstalling,
              click below to restore your purchase.
            </p>
            <IonButton
              expand="block"
              fill="outline"
              onClick={handleRestorePurchases}
              disabled={restoring}
            >
              {restoring ? <IonSpinner name="crescent" /> : 'Restore Purchases'}
            </IonButton>
          </IonCardContent>
        </IonCard>

        {/* Support Message */}
        <IonCard style={{ background: 'var(--ion-color-light)' }}>
          <IonCardContent>
            <p style={{ fontSize: '14px', lineHeight: '1.6', margin: 0, textAlign: 'center', color: 'var(--ion-color-dark)' }}>
              <strong>Questions about your subscription?</strong><br />
              Contact support and I'll help you out personally!
            </p>
            <IonButton
              expand="block"
              fill="clear"
              size="small"
              href="mailto:support@dice-cat.com"
              style={{ marginTop: '12px' }}
            >
              Email Support
            </IonButton>
          </IonCardContent>
        </IonCard>

        <IonNote style={{
          display: 'block',
          textAlign: 'center',
          fontSize: '12px',
          marginTop: '24px',
          padding: '0 16px'
        }}>
          Subscriptions are managed through your device's app store.
          Charges will appear on your app store account.
        </IonNote>
      </IonContent>

      <PremiumPaywall
        isOpen={showPaywall}
        onDismiss={() => setShowPaywall(false)}
        feature="upgrade"
      />
    </IonPage>
  );
};